import React, { useEffect, useState, useRef } from 'react';
import { t } from 'i18next';
import { KTIcon, toAbsoluteUrl } from '../../../_metronic/helpers';
import { useReactToPrint } from 'react-to-print';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import fetchAPI from '../../../common/fetchAPI';
import moment from 'moment';
import { formatDateWithDateAndMonthName, FormatInvoiceDecimalNumber, getValueFromResource } from '../../../common/timeLiveFunction';
import InvoicePrintWidgetCustom from './InvoicePrintWidgetCustom';
import { updateTimeExpenseBillingIdForPrint } from '../invoiceBillingSlice';
import NotoSansThaiRegular from '../../../assets/fonts/NotoSansThai-Regular.ttf';
import NotoNaskhArabicRegular from '../../../assets/fonts/NotoNaskhArabic-Regular.ttf';
import NotoSansDevanagariRegular from '../../../assets/fonts/NotoSansDevanagari-Regular.ttf';

const objRecords = {
    timesheetArray: [],
    masterRecordArray: [],
    expenseArray: [],
    masterArray: [],
    taxArray: [],
    isLoading: true,
    logoPath: ''
}

const PX = 25.4 / 96;
const mm = (px: number) => px * PX;

const wrapPdfText = (pdf: jsPDF, text: string, widthMm: number) => {
    const value = String(text ?? '').replace(/\s+/g, ' ').trim();
    if (!value) {
        return [];
    }
    return pdf.splitTextToSize(value, widthMm);
};

const loadFontAsBase64 = async (fontPath: string): Promise<string> => {
    const response = await fetch(fontPath);
    if (!response.ok) {
        throw new Error(`Failed to load font: ${fontPath}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(
            i,
            Math.min(i + chunkSize, bytes.length)
        );
        binary += String.fromCharCode.apply(
            null,
            Array.from(chunk)
        );
    }
    return btoa(binary);
};

const getPDFFontForText = (text: string): string => {
    if (!text) {
        return "helvetica";
    }
    if (/[\u0E00-\u0E7F]/.test(text)) {
        return "NotoSansThai";
    }
    if (/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(text)) {
        return "NotoNaskhArabic";
    }
    if (/[\u0900-\u097F]/.test(text)) {
        return "NotoSansDevanagari";
    }
    return "helvetica";
};

const resolveInvoiceLogoSrc = (showLogo: boolean, logoPath: string) => {
    if (!showLogo) {
        return '';
    }
    const path = String(logoPath ?? '').trim();
    const source = path.split('?')[0];
    if (!source || source === 'null' || source === 'undefined') {
        return '';
    }
    return path;
};

const readInvoiceLogo = (img: HTMLImageElement | null) => {
    if (!img || !img.complete || !img.naturalWidth) {
        return '';
    }
    try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const context = canvas.getContext('2d');
        if (!context) {
            return '';
        }
        context.drawImage(img, 0, 0);
        return canvas.toDataURL('image/png');
    } catch {
        return '';
    }
};

const InvoicePrintViewWidgetCustom = (props: any) => {
    const dispatch = useAppDispatch();
    const { modelRef } = props;
    const componentRef = useRef<HTMLDivElement>(null);
    const { accountTimeExpenseBillingIdForPrint, accountTimeExpenseBillingId, masterAccountTimeExpenseBilling } = useAppSelector((state) => state.invoiceBilling);
    const { decimalPlaces, cultureInfoName, sessionAccountId, showProjectNameInInvoiceReport, showBillingRateInInvoiceReport, showEntryDateInInvoiceReport, showEmployeeNameInInvoiceReport,
        isCompanyOwnLogo, manageTerminologyList
    } = useAppSelector(state => state.globalState);
    const [invoiceDetail, setInvoiceDetail] = useState<any | ''>(objRecords);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const getMasterRecord: any = masterAccountTimeExpenseBilling[0];

    useEffect(() => {
        if (accountTimeExpenseBillingIdForPrint == '') {
            setInvoiceDetail({ ...objRecords });
            return;
        }
        let isCurrent = true;
        setInvoiceDetail((prev: any) => ({ ...prev, isLoading: true }));
        getAccountInvoiceRecords(accountTimeExpenseBillingIdForPrint, () => !isCurrent);
        return () => {
            isCurrent = false;
        };
    }, [accountTimeExpenseBillingIdForPrint]);

    useEffect(() => {
        const modalEl = document.getElementById('invoicePrintModal');
        if (!modalEl) {
            return;
        }
        const onHidden = () => {
            dispatch(updateTimeExpenseBillingIdForPrint(''));
            setInvoiceDetail({ ...objRecords });
        };
        modalEl.addEventListener('hidden.bs.modal', onHidden);
        return () => modalEl.removeEventListener('hidden.bs.modal', onHidden);
    }, [dispatch]);

    let object = {
        "AccountName": "",
        "AccountAddress1": "",
        "AccountAddress2": "",
        "City": "",
        "ZipCode": "",
        "AccountCountry": "",
        "Telephone": "",
        "InvoiceNumber": "",
        "PONumber": "",
        "InvoiceDate": "",
        "DueDate": "",
        "ClientName": "",
        "Address1": "",
        "Address2": "",
        "ClientCity": "",
        "ClientState": "",
        "ClientZipcode": "",
        "ClientCountry": "",
        "ClientTelephone": "",
        "ProjectName": "",
        "StartDate": "",
        "EndDate": "",
        "SubTotal": "",
        "Discount": "",
        "GrandTotal": "",
        "TotalTimesheetAmount": 0,
        "TotalExpenseAmount": 0,
        "CurrencyCode": "",
        "MasterDescription": "",
        "InvoiceNoPrefix": "",
        "TotalBillHours": "",
        "MasterProjectName": ""
    }

    const timestamp = new Date().getTime();
    const getAccountInvoiceRecords = async (billingId: string, isStale?: () => boolean) => {
        try {
        const response = await fetchAPI(`api/V2/Invoice/GetReportData/${sessionAccountId}/${billingId}/${isCompanyOwnLogo}`, 'Get', '', true);
        const json = await response.json();
        let tempMasterArray: any[] = [];
        let result: any[] = [];
        if (json["TimesheetTax"].length > 0 || json["ExpenseTax"].length > 0) {
            const combinedData = [...json["TimesheetTax"], ...json["ExpenseTax"]];
            const taxSums: any = {};
            combinedData.forEach(item => {
                if (item.TaxCodeName1 != null) {
                    const name1 = item.TaxCodeName1;
                    if (taxSums[name1]) {
                        taxSums[name1] += item.TaxCode1;
                    } else {
                        taxSums[name1] = item.TaxCode1;
                    }
                }
                if (item.TaxCodeName2 != null) {
                    const name2 = item.TaxCodeName2;
                    if (taxSums[name2]) {
                        taxSums[name2] += item.TaxCode2;
                    } else {
                        taxSums[name2] = item.TaxCode2;
                    }
                }
            });
            result = Object.keys(taxSums).map(key => ({
                TaxCodeName: key,
                TotalTaxCode: taxSums[key].toFixed(2)
            }));
        } else {
            result = [];
        }
        if (json["MasterRecord"].length > 0) {
            json["MasterRecord"].map((item: any) => {
                let isInvoiceTemplate = (item.InvoiceTemplateAccountName === "" || item.InvoiceTemplateAccountName === null) ? false : true;
                object.AccountName = isInvoiceTemplate ? item.InvoiceTemplateAccountName : item.AccountName;
                object.AccountAddress1 = isInvoiceTemplate ? item.InvoiceTemplateAddress1 : item.AccountAddress1;
                object.AccountAddress2 = isInvoiceTemplate ? item.InvoiceTemplateAddress2 : item.AccountAddress2;
                object.City = isInvoiceTemplate ? item.InvoiceTemplateCity : item.City;
                object.ZipCode = isInvoiceTemplate ? item.InvoiceTemplateZipCode : item.ZipCode;
                object.AccountCountry = isInvoiceTemplate ? item.InvoiceTemplateCountry : item.AccountCountry;
                object.Telephone = isInvoiceTemplate ? item.InvoiceTemplateTelephone : item.Telephone;
                object.InvoiceNumber = item.InvoiceNumber;
                object.PONumber = item.PONumber;
                object.InvoiceDate = formatDateWithDateAndMonthName(item.InvoiceDate, cultureInfoName.toString());
                object.DueDate = formatDateWithDateAndMonthName(item.DueDate, cultureInfoName.toString());
                object.ClientName = item.ClientName;
                object.Address1 = item.Address1;
                object.Address2 = item.Address2;
                object.ClientCity = item.ClientCity;
                object.ClientState = item.ClientState;
                object.ClientZipcode = item.ClientZipcode;
                object.ClientCountry = item.ClientCountry;
                object.ClientTelephone = item.ClientTelephone;
                object.ProjectName = item.ProjectName;
                object.StartDate = formatDateWithDateAndMonthName(item.BillingCycleStartDate, cultureInfoName.toString());//moment(item.BillingCycleStartDate).format("DD/MM/YYYY");
                object.EndDate = formatDateWithDateAndMonthName(item.BillingCycleEndDate, cultureInfoName.toString()); //moment(item.BillingCycleEndDate).format("DD/MM/YYYY");
                object.SubTotal = item.SubTotal;
                object.Discount = item.Discount;
                object.GrandTotal = item.GrandTotal;
                object.CurrencyCode = item.CurrencyCode;
                // object.MasterDescription = item["Master Description"].split('\n').map((item: any) => item.trim()).filter((item: any) => item !== '');
                object.MasterDescription = item["Master Description"];
                object.InvoiceNoPrefix = item.InvoiceNoPrefix;
                object.MasterProjectName = item.MasterProjectName;
            });
            let total = 0;
            json["MasterRecord"].map((item: any) => {
                total = total + item.TotalAmount
            })
            object.TotalTimesheetAmount = total;
            let totalBillHours = 0;
            json["MasterRecord"].map((item: any) => {
                totalBillHours = totalBillHours + item.BillHours;
            })
            object.TotalBillHours = totalBillHours.toFixed(2);
            tempMasterArray.push(object);
        }
        if (json["ExpenseSheet"].length > 0) {
            let total = 0;
            json["ExpenseSheet"].map((item: any) => {
                total = total + Number(item.BilledExpenseAmount);
            })
            object.TotalExpenseAmount = Number(total);
            if (json["MasterRecord"].length == 0) {
                let taxAmount = 0;
                result.map((item) => {
                    taxAmount = taxAmount + Number(item.TotalTaxCode);
                });
                object.SubTotal = "0";
                object.Discount = "0";
                object.GrandTotal = (total + taxAmount).toString();
            }
            tempMasterArray.push(object);
        }
        if (isStale?.()) {
            return;
        }
        setInvoiceDetail({
            ...invoiceDetail,
            masterRecordArray: json["MasterRecord"],
            expenseArray: json["ExpenseSheet"],
            masterArray: tempMasterArray?.[0] ?? {},
            taxArray: result,
            isLoading: false,
            logoPath: json["ImageURL"] + "?_=" + timestamp,
            timesheetArray: json["Timesheet"]
        });
        } catch (error) {
            if (isStale?.()) {
                return;
            }
            setInvoiceDetail((prev: any) => ({ ...prev, isLoading: false }));
        }
    }

    const { masterArray, taxArray, logoPath } = invoiceDetail;
    const logoSrc = resolveInvoiceLogoSrc(Boolean(isCompanyOwnLogo), logoPath);
    const invoiceNumber = masterArray?.InvoiceNoPrefix ? `${masterArray.InvoiceNoPrefix}-${masterArray.InvoiceNumber}` : (masterArray?.InvoiceNumber || 'Invoice');

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: `Invoice-${invoiceNumber}`
    });

    const handleDownloadPdf = async () => {
        if (isDownloadingPdf || invoiceDetail.isLoading || !masterArray || Array.isArray(masterArray)) {
            return;
        }
        setIsDownloadingPdf(true);
        try {
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const marginX = 14;
            const contentWidth = pageWidth - marginX * 2;
            const textRight = pageWidth - marginX;
            const nameWidth = 52;
            const infoWidth = 48;
            const lineH = 4.4;
            const fontSize = 8;
            const pageNumberY = pageHeight - 5;
            const label = (key: string) => getValueFromResource(key, dispatch, t);
            const money = (currency: any, value: any) => `${currency || ''} ${FormatInvoiceDecimalNumber(cultureInfoName.toString(), value, decimalPlaces)}`.trim();
            // const logoData = isCompanyOwnLogo
            // ? readInvoiceLogo(componentRef.current?.querySelector('img') ?? null)
            const logoSrc = resolveInvoiceLogoSrc(Boolean(isCompanyOwnLogo), logoPath);
            const logoData = logoSrc
                ? readInvoiceLogo(componentRef.current?.querySelector('img.invoice-company-logo') ?? null)
                : '';
            const logoWidth = mm(180);
            const logoHeight = mm(50);
            const logoPad = mm(10);
            const logoTop = 8 + logoPad;
            const companyLineH = 3.4;

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(fontSize);
            const nameLines = wrapPdfText(pdf, String(masterArray.AccountName || ''), nameWidth);
            pdf.setFont('helvetica', 'normal');
            const addressLines = [
                ...wrapPdfText(pdf, String(masterArray.AccountAddress1 || ''), infoWidth),
                ...wrapPdfText(pdf, String(masterArray.AccountAddress2 || ''), infoWidth),
                ...wrapPdfText(pdf, `${masterArray.City || ''} ${masterArray.ZipCode || ''}`.trim(), infoWidth),
                ...wrapPdfText(pdf, String(masterArray.AccountCountry || ''), infoWidth),
                ...wrapPdfText(pdf, String(masterArray.Telephone || ''), infoWidth),
            ];
            const headerTop = logoTop + logoHeight + logoPad + mm(8);
            const headerBottom = headerTop + (nameLines.length + addressLines.length) * companyLineH + 8;
            const footerText = Array.isArray(masterArray.MasterDescription)
                ? masterArray.MasterDescription.map((item: any) => String(item ?? '').replace(/\t/g, '    ')).join('\n')
                : String(masterArray.MasterDescription ?? '').replace(/\t/g, '    ');
            const footerParts = footerText.split('\n');
            const footerFonts = new Set(footerParts.map((part: string) => getPDFFontForText(part)));
            if (footerFonts.has("NotoSansThai")) {
                const notoThaiRegularBase64 = await loadFontAsBase64(NotoSansThaiRegular);
                pdf.addFileToVFS("NotoSansThai-Regular.ttf", notoThaiRegularBase64);
                pdf.addFont("NotoSansThai-Regular.ttf", "NotoSansThai", "normal");
            }
            if (footerFonts.has("NotoNaskhArabic")) {
                const notoArabicRegularBase64 = await loadFontAsBase64(NotoNaskhArabicRegular);
                pdf.addFileToVFS("NotoNaskhArabic-Regular.ttf", notoArabicRegularBase64);
                pdf.addFont("NotoNaskhArabic-Regular.ttf", "NotoNaskhArabic", "normal");
            }
            if (footerFonts.has("NotoSansDevanagari")) {
                const notoDevanagariRegularBase64 = await loadFontAsBase64(NotoSansDevanagariRegular);
                pdf.addFileToVFS("NotoSansDevanagari-Regular.ttf", notoDevanagariRegularBase64);
                pdf.addFont("NotoSansDevanagari-Regular.ttf", "NotoSansDevanagari", "normal");
            }
            pdf.setFontSize(fontSize);
            const footerLines: Array<{ text: string; font: string }> = [];
            footerParts.forEach((part: string) => {
                const font = getPDFFontForText(part);
                pdf.setFont(font, "normal");
                const wrapped = wrapPdfText(pdf, part, contentWidth);
                if (wrapped.length) {
                    wrapped.forEach((line: string) => footerLines.push({ text: line, font }));
                } else {
                    footerLines.push({ text: '', font });
                }
            });
            const footerHeight = footerLines.length * lineH;
            const marginBottom = 12 + footerHeight + 3;

            const paintChrome = () => {
                const total = pdf.getNumberOfPages();
                for (let page = 1; page <= total; page++) {
                    pdf.setPage(page);
                    if (logoData) {
                        pdf.addImage(logoData, 'PNG', textRight - logoWidth, logoTop, logoWidth, logoHeight);
                    }
                    let headerY = headerTop;
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(fontSize);
                    pdf.setTextColor(0, 0, 0);
                    nameLines.forEach((line: string) => {
                        pdf.text(line, textRight, headerY, { align: 'right' });
                        headerY += companyLineH;
                    });
                    pdf.setFont('helvetica', 'normal');
                    addressLines.forEach((line: string) => {
                        pdf.text(line, textRight, headerY, { align: 'right' });
                        headerY += companyLineH;
                    });
                    if (footerHeight) {
                        let footerY = pageHeight - 12 - footerLines.length * lineH;
                        pdf.setFontSize(fontSize);
                        pdf.setTextColor(0, 0, 0);
                        footerLines.forEach((line) => {
                            if (line.text) {
                                pdf.setFont(line.font, "normal");
                                pdf.text(line.text, marginX, footerY);
                            }
                            footerY += lineH;
                        });
                    }
                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(8);
                    pdf.setTextColor(58, 141, 196);
                    pdf.text(`Page ${page} of ${total}`, textRight, pageNumberY, { align: 'right' });
                }
            };

            let y = headerBottom;
            const invoiceNo = masterArray.InvoiceNoPrefix === ''
                ? String(masterArray.InvoiceNumber ?? '')
                : `${masterArray.InvoiceNoPrefix}-${masterArray.InvoiceNumber}`;
            const metaRows = [
                [label('Invoice No.'), invoiceNo],
                [label('Invoice Date:'), String(masterArray.InvoiceDate ?? '')],
                [label('PO Number:'), String(masterArray.PONumber ?? '')],
                [label('Due Date:'), String(masterArray.DueDate ?? '')],
            ];
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(fontSize);
            pdf.setTextColor(0, 0, 0);
            let maxValueW = mm(40);
            let maxLabelW = mm(40);
            metaRows.forEach(([metaLabel, metaValue]) => {
                maxLabelW = Math.max(maxLabelW, pdf.getTextWidth(String(metaLabel)));
                maxValueW = Math.max(maxValueW, pdf.getTextWidth(String(metaValue)));
            });
            const valueX = textRight;
            const labelX = textRight - maxValueW - 3;
            let metaY = y;
            metaRows.forEach(([metaLabel, metaValue]) => {
                pdf.text(String(metaLabel), labelX, metaY, { align: 'right' });
                pdf.text(String(metaValue), valueX, metaY, { align: 'right' });
                metaY += lineH;
            });

            let leftY = y;
            const leftWidth = Math.max(70, labelX - marginX - 8);
            const writeLeft = (text: string, bold = false, width = leftWidth) => {
                const parts = wrapPdfText(pdf, text, width);
                if (!parts.length) {
                    return;
                }
                pdf.setFont('helvetica', bold ? 'bold' : 'normal');
                pdf.setFontSize(fontSize);
                parts.forEach((part: string) => {
                    pdf.text(part, marginX, leftY);
                    leftY += lineH;
                });
            };
            writeLeft(label('Billing Address :'), true);
            writeLeft(String(masterArray.ClientName || ''), false);
            writeLeft(String(masterArray.Address1 || ''), false);
            if (masterArray.Address2 != '') {
                writeLeft(String(masterArray.Address2 || ''), false);
            }
            writeLeft(`${masterArray.ClientCity || ''} ${masterArray.ClientState || ''} ${masterArray.ClientZipcode || ''}`.trim(), false);
            writeLeft(String(masterArray.ClientCountry || ''), false);
            leftY += 3;
            writeLeft(`RE : - ${masterArray.MasterProjectName || ''}`, true);
            leftY += 3;
            writeLeft(label('Billing Period'), true);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(fontSize);
            const startLabel = label('Start Date:');
            const endLabel = label('End Date:');
            const startLabelW = pdf.getTextWidth(startLabel);
            const endLabelW = pdf.getTextWidth(endLabel);
            pdf.text(startLabel, marginX, leftY);
            pdf.setFont('helvetica', 'normal');
            pdf.text(String(masterArray.StartDate ?? ''), marginX + startLabelW + 2, leftY);
            leftY += lineH;
            pdf.setFont('helvetica', 'bold');
            pdf.text(endLabel, marginX, leftY);
            pdf.setFont('helvetica', 'normal');
            pdf.text(String(masterArray.EndDate ?? ''), marginX + endLabelW + 2, leftY);
            leftY += lineH;
            y = Math.max(metaY, leftY) + 6;

            type PdfColumn = { key: string; header: string; width: number; align: 'left' | 'center' | 'right'; value: (item: any) => string };
            const fitColumns = (columns: PdfColumn[]) => {
                const total = columns.reduce((sum, column) => sum + column.width, 0);
                columns.forEach((column) => {
                    column.width = contentWidth * (column.width / total);
                });
            };
            const drawTable = (columns: PdfColumn[], items: any[], foot: string[], totalIndex: number) => {
                if (!items.length) {
                    return;
                }
                if (y > pageHeight - marginBottom - mm(40)) {
                    pdf.addPage();
                    y = headerBottom;
                }
                const columnStyles: Record<number, { cellWidth: number; halign: 'left' | 'center' | 'right' }> = {};
                columns.forEach((column, index) => {
                    columnStyles[index] = { cellWidth: column.width, halign: column.align };
                });
                autoTable(pdf, {
                    startY: y,
                    margin: { top: headerBottom, bottom: marginBottom, left: marginX, right: marginX },
                    head: [columns.map((column) => column.header)],
                    body: items.map((item) => columns.map((column) => column.value(item))),
                    foot: [foot],
                    showHead: 'firstPage',
                    showFoot: 'lastPage',
                    theme: 'plain',
                    rowPageBreak: 'avoid',
                    styles: {
                        font: 'helvetica',
                        fontSize: fontSize,
                        textColor: [0, 0, 0],
                        cellPadding: { top: 1.1, right: 0.6, bottom: 1.1, left: 0.6 },
                        overflow: 'linebreak',
                        valign: 'top',
                    },
                    headStyles: {
                        fillColor: [51, 102, 153],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                        halign: 'center',
                        valign: 'middle',
                        fontSize: fontSize,
                        lineColor: [255, 255, 255],
                        lineWidth: 0.5,
                        minCellHeight: mm(29),
                    },
                    footStyles: {
                        fillColor: [255, 255, 255],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        fontSize: fontSize,
                        valign: 'middle',
                        minCellHeight: mm(35),
                    },
                    columnStyles,
                    didParseCell: (data: any) => {
                        if (data.section === 'body') {
                            data.cell.styles.lineWidth = { top: 0.15, right: 0, bottom: 0, left: 0 };
                            data.cell.styles.lineColor = [192, 192, 192];
                            data.cell.styles.fillColor = data.row.index % 2 === 1 ? [246, 246, 246] : [255, 255, 255];
                        }
                        if (data.section === 'foot') {
                            data.cell.styles.lineWidth = { top: 0.25, right: 0, bottom: 0.25, left: 0 };
                            data.cell.styles.lineColor = [0, 0, 0];
                            data.cell.styles.fillColor = [255, 255, 255];
                            const column = columns[data.column.index];
                            if (column) {
                                data.cell.styles.halign = column.align;
                            }
                            if (data.column.index === totalIndex) {
                                data.cell.styles.halign = 'right';
                            }
                        }
                    },
                });
                y = ((pdf as any).lastAutoTable?.finalY ?? y) + mm(36);
            };

            if (invoiceDetail.timesheetArray.length > 0) {
                const columns: PdfColumn[] = [];
                if (showEntryDateInInvoiceReport) {
                    columns.push({
                        key: 'date',
                        header: label('Entry Date'),
                        width: 90,
                        align: 'left',
                        value: (item) => item.TimeEntryDate ? moment(item.TimeEntryDate).format('DD/MM/YYYY') : '',
                    });
                }
                if (showProjectNameInInvoiceReport) {
                    columns.push({
                        key: 'project',
                        header: label('Project Name'),
                        width: 90,
                        align: 'left',
                        value: (item) => String(item.ProjectName ?? ''),
                    });
                }
                if (showEmployeeNameInInvoiceReport) {
                    columns.push({
                        key: 'employee',
                        header: label('Employee Name'),
                        width: 109,
                        align: 'left',
                        value: (item) => String(item.EmployeeName ?? ''),
                    });
                }
                columns.push({
                    key: 'description',
                    header: label('Timesheet Description'),
                    width: 173,
                    align: 'left',
                    value: (item) => String(item['Detail Description'] ?? ''),
                });
                if (showBillingRateInInvoiceReport) {
                    columns.push({
                        key: 'rate',
                        header: label('Billing Rate'),
                        width: 90,
                        align: 'right',
                        value: (item) => money(item.CurrencyCode, item.ActualBillingRate),
                    });
                }
                columns.push({
                    key: 'hours',
                    header: label('Billing Hours'),
                    width: 90,
                    align: 'center',
                    value: (item) => FormatInvoiceDecimalNumber(cultureInfoName.toString(), item.BillHours, 2),
                });
                columns.push({
                    key: 'amount',
                    header: label('Amount'),
                    width: 108,
                    align: 'right',
                    value: (item) => money(item.CurrencyCode, item.TotalAmount),
                });
                fitColumns(columns);
                const foot = columns.map(() => '');
                const hoursIndex = columns.findIndex((column) => column.key === 'hours');
                const amountIndex = columns.findIndex((column) => column.key === 'amount');
                const rateIndex = columns.findIndex((column) => column.key === 'rate');
                const totalIndex = rateIndex >= 0 ? rateIndex : Math.max(hoursIndex - 1, 0);
                foot[totalIndex] = 'Total:';
                foot[hoursIndex] = FormatInvoiceDecimalNumber(cultureInfoName.toString(), masterArray.TotalBillHours, 2);
                foot[amountIndex] = money(masterArray.CurrencyCode, masterArray.TotalTimesheetAmount);
                drawTable(columns, invoiceDetail.timesheetArray, foot, totalIndex);
            }

            if (invoiceDetail.expenseArray.length > 0) {
                const columns: PdfColumn[] = [];
                if (showProjectNameInInvoiceReport) {
                    columns.push({
                        key: 'project',
                        header: label('Project Name'),
                        width: 167,
                        align: 'left',
                        value: (item) => String(item.ProjectName ?? ''),
                    });
                }
                columns.push({
                    key: 'description',
                    header: label('Expense Description'),
                    width: 408,
                    align: 'left',
                    value: (item) => String(item.Description ?? ''),
                });
                columns.push({
                    key: 'amount',
                    header: label('Billed Amount'),
                    width: 104,
                    align: 'right',
                    value: (item) => money(item.CurrencyCode, item.BilledExpenseAmount),
                });
                fitColumns(columns);
                const foot = columns.map(() => '');
                const amountIndex = columns.findIndex((column) => column.key === 'amount');
                const totalIndex = Math.max(amountIndex - 1, 0);
                foot[totalIndex] = 'Total:';
                foot[amountIndex] = money(masterArray.CurrencyCode, masterArray.TotalExpenseAmount);
                drawTable(columns, invoiceDetail.expenseArray, foot, totalIndex);
            }

            const totalRows: Array<[string, string]> = [
                [label('Sub Total:'), money(masterArray.CurrencyCode, masterArray.SubTotal)],
                [label('Discount'), `- ${money(masterArray.CurrencyCode, masterArray.Discount)}`],
            ];
            if (taxArray.length > 0 && taxArray[0].TaxCodeName != 'null') {
                taxArray.forEach((item: any) => {
                    totalRows.push([`${label(item.TaxCodeName)}:`, money(masterArray.CurrencyCode, item.TotalTaxCode)]);
                });
            }
            totalRows.push([label('Grand Total:'), money(masterArray.CurrencyCode, masterArray.GrandTotal)]);
            const totalsHeight = totalRows.length * lineH + 8;
            if (y + totalsHeight > pageHeight - marginBottom) {
                pdf.addPage();
                y = headerBottom + 6;
            }
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(fontSize);
            let totalLabelW = 0;
            let totalValueW = 0;
            totalRows.forEach(([totalLabel, totalValue]) => {
                totalLabelW = Math.max(totalLabelW, pdf.getTextWidth(totalLabel));
                totalValueW = Math.max(totalValueW, pdf.getTextWidth(totalValue));
            });
            const totalsValueX = textRight;
            const totalsLabelX = textRight - totalValueW - 4;
            const totalsLeft = totalsLabelX - totalLabelW;
            totalRows.forEach(([totalLabel, totalValue], index) => {
                const isGrand = index === totalRows.length - 1;
                if (isGrand) {
                    y += 1.2;
                    pdf.setDrawColor(0, 0, 0);
                    pdf.setLineWidth(0.2);
                    pdf.line(totalsLeft, y - 3.2, textRight, y - 3.2);
                }
                pdf.setTextColor(0, 0, 0);
                pdf.text(totalLabel, totalsLabelX, y, { align: 'right' });
                pdf.text(totalValue, totalsValueX, y, { align: 'right' });
                if (isGrand) {
                    pdf.line(totalsLeft, y + 1.5, textRight, y + 1.5);
                }
                y += lineH;
            });

            paintChrome();
            pdf.save(`Invoice-${invoiceNumber}.pdf`);
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    const getLabelNameFromTerminology = (Value: string) => {
        if (manageTerminologyList && manageTerminologyList.length > 0) {
            for (let manageTerminologyItem of manageTerminologyList) {
                let isManageTerminologyExist: boolean = Value.includes(manageTerminologyItem.TerminologyName);
                if (isManageTerminologyExist) {
                    return Value.replace(Value, manageTerminologyItem.UserDefinedName).toString();
                }
            }
        }
        return Value.toString();
    }

    return (
        <>
            <div className='modal drawer-overlay' tabIndex={-1} data-bs-backdrop="false" id='invoicePrintModal' role="dialog" aria-labelledby="exampleModalSizeSm" aria-hidden="true">
                <div className='modal-dialog modal-xl'>
                    <div className='modal-content rounded'>
                        <div className='modal-header pb-2'>
                            <h3 className='headerTitle'>
                                {getValueFromResource('Invoice Detail', dispatch, t)}
                            </h3>
                            <div className='btn btn-sm btn-icon btn-active-color-primary' data-bs-dismiss='modal'>
                                <KTIcon iconName='cross' className='fs-1' />
                            </div>
                        </div>
                        <div className='modal-body pt-0 pb-15 px-5 px-xl-5' style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
                            {invoiceDetail.isLoading ? <div className="d-flex justify-content-center align-items-center">
                                <span className='indicator-progress' style={{ display: 'block' }}>
                                    <span className='spinner-border spinner-border-md ms-2 mt-12'></span>
                                </span>
                            </div> : <>
                                <div className='row text-left mb-3 mt-3'>
                                {/* {isCompanyOwnLogo && (
                                        <div className='d-flex justify-content-end'>
                                            <img
                                                src={logoPath}
                                                alt={logoPath} style={{ width: '180px', height: '50px' }} />
                                        </div>)} */}
                                    <div className='d-flex justify-content-end' style={{ paddingTop: 10, paddingBottom: 10 }}>
                                        <div style={{ width: 180, height: 50 }}>
                                            {logoSrc ? (
                                                <img
                                                    className='invoice-company-logo'
                                                    src={logoSrc}
                                                    alt=''
                                                    style={{ width: '180px', height: '50px', display: 'block' }}
                                                    onError={(event) => { event.currentTarget.style.visibility = 'hidden'; }}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div className='invoice-company-header'>
                                    {/* <span className='invoice-company-name'>{masterArray.AccountName}</span><br />
                                        <span className='invoice-info-right'>{masterArray.AccountAddress1}</span><br />
                                        <span className='invoice-info-right'>{masterArray.AccountAddress2}</span><br />
                                        <span className='invoice-info-right'>{masterArray.City + ' ' + masterArray.ZipCode}</span><br />
                                        <span className='invoice-info-right'>{masterArray.AccountCountry}</span><br />
                                        <span className='invoice-info-right'>{masterArray.Telephone}</span><br /><br /> */}
                                            <span className='invoice-company-name'>{masterArray.AccountName}</span>
                                            <span className='invoice-info-right invoice-company-line'>{masterArray.AccountAddress1}</span>
                                            <span className='invoice-info-right invoice-company-line'>{masterArray.AccountAddress2}</span>
                                            <span className='invoice-info-right invoice-company-line'>{masterArray.City + ' ' + masterArray.ZipCode}</span>
                                            <span className='invoice-info-right invoice-company-line'>{masterArray.AccountCountry}</span>
                                            <span className='invoice-info-right invoice-company-line'>{masterArray.Telephone}</span>
                                        </div>
                                        <br />
                                        <span className='invoice-info-right' style={{ width: 84, fontWeight: 'bold', marginTop: 15 }}>
                                            {getValueFromResource('Invoice No.', dispatch, t)}
                                        </span>
                                        <span className='invoice-info-right' style={{ width: 84, fontWeight: 'bold', marginTop: 15 }}>{masterArray.InvoiceNoPrefix === '' ? masterArray.InvoiceNumber : masterArray.InvoiceNoPrefix + '-' + masterArray.InvoiceNumber}</span><br />
                                        <span className='invoice-info-right' style={{ width: 84, fontWeight: 'bold' }}>
                                            {getValueFromResource('Invoice Date:', dispatch, t)}
                                        </span>
                                        <span className='invoice-info-right' style={{ width: 84, fontWeight: 'bold' }}>{masterArray.InvoiceDate}</span><br />
                                        <span className='invoice-info-right' style={{ width: 84, fontWeight: 'bold' }}>
                                            {getValueFromResource('PO Number:', dispatch, t)}
                                        </span>
                                        <span className='invoice-info-right' style={{ width: 84, fontWeight: 'bold' }}>{masterArray.PONumber}</span><br />
                                        <span className='invoice-info-right' style={{ width: 84, fontWeight: 'bold' }}>
                                            {getValueFromResource('Due Date:', dispatch, t)}
                                        </span>
                                        <span className='invoice-info-right' style={{ width: 84, fontWeight: 'bold' }}>{masterArray.DueDate}</span><br />
                                    </div>
                                    <div style={{ marginLeft: 0, marginTop: -28 }}>
                                        <span className='invoice-company-address' style={{ minHeight: 0 }}>
                                            {getValueFromResource('Billing Address :', dispatch, t)}
                                        </span> <br />
                                        <span className='invoice-info-left'>{masterArray.ClientName}</span><br />
                                        <span className='invoice-info-left' style={{ width: 200 }}>{masterArray.Address1}</span><br />
                                        {masterArray.Address2 != "" && <><span className='invoice-info-left'>{masterArray.Address2}</span><br /></>}
                                        <span className='invoice-info-left' style={{ width: 200, marginTop: 16 }}>{`${masterArray.ClientCity} ${masterArray.ClientState} ${masterArray.ClientZipcode}`}</span><br />
                                        <span className='invoice-info-left'>{masterArray.ClientCountry}</span><br />
                                        <span className='invoice-company-address mt-4 mb-4' style={{ minHeight: 0 }}>
                                            {'RE : - ' + masterArray.MasterProjectName}
                                        </span> <br />
                                        <span className='invoice-company-address' style={{ minHeight: 0 }}>
                                            {getValueFromResource('Billing Period', dispatch, t)}
                                        </span> <br />
                                        <span className='invoice-info-left' style={{ width: 84, fontWeight: 'bold', paddingRight: 4 }}>
                                            {getValueFromResource('Start Date:', dispatch, t)}
                                        </span>
                                        <span className='invoice-info-left'>{masterArray.StartDate}</span><br />
                                        <span className='invoice-info-left' style={{ width: 84, fontWeight: 'bold', paddingRight: 4 }}>
                                            {getValueFromResource('End Date:', dispatch, t)}
                                        </span>
                                        <span className='invoice-info-left'>{masterArray.EndDate}</span><br />
                                    </div>
                                </div>
                                {invoiceDetail.timesheetArray.length > 0 &&
                                    <table className='invoiceTable mb-9'>
                                        <thead>
                                            <tr>
                                                {showEntryDateInInvoiceReport && (
                                                    <th className='invoiceGridTH' style={{ width: 90 }}>{getValueFromResource('Entry Date', dispatch, t)}</th>
                                                )}
                                                {showProjectNameInInvoiceReport && (
                                                    <th className='invoiceGridTH' style={{ width: 90 }}>{getValueFromResource('Project Name', dispatch, t)}</th>
                                                )}
                                                {showEmployeeNameInInvoiceReport && (
                                                    <th className='invoiceGridTH' style={{ width: 109 }}>{getValueFromResource('Employee Name', dispatch, t)}</th>
                                                )}
                                                <th className='invoiceGridTH' style={{ width: 173 }}>{getValueFromResource('Timesheet Description', dispatch, t)}</th>
                                                {showBillingRateInInvoiceReport && (
                                                    <th className='invoiceGridTH' style={{ width: 90 }}>{getValueFromResource('Billing Rate', dispatch, t)}</th>
                                                )}
                                                <th className='invoiceGridTH' style={{ width: 90 }}>{getValueFromResource('Billing Hours', dispatch, t)}</th>
                                                <th className='invoiceGridTH' style={{ width: 108 }}>{getValueFromResource('Amount', dispatch, t)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoiceDetail.timesheetArray.length > 0 &&
                                                invoiceDetail.timesheetArray.map((item: any, index: number) => (
                                                    <tr key={index} className='invoiceBody'>
                                                        {showEntryDateInInvoiceReport && (
                                                            <td className='invoiceTD'>
                                                                {item.TimeEntryDate ? moment(item.TimeEntryDate).format('DD/MM/YYYY') : ''}
                                                            </td>
                                                        )}
                                                        {showProjectNameInInvoiceReport && (
                                                            <td className='invoiceTD'>{item.ProjectName}</td>
                                                        )}
                                                        {showEmployeeNameInInvoiceReport && (
                                                            <td className='invoiceTD'>{item.EmployeeName ? item.EmployeeName : ''}</td>
                                                        )}
                                                        <td className='invoiceTD'>{item['Detail Description']}</td>
                                                        {showBillingRateInInvoiceReport && (
                                                            <td className='invoiceTD text-right'>
                                                                {item.CurrencyCode + ' ' + FormatInvoiceDecimalNumber(cultureInfoName.toString(), item.ActualBillingRate, decimalPlaces)}
                                                            </td>
                                                        )}
                                                        <td className='invoiceTD text-center'>
                                                            {FormatInvoiceDecimalNumber(cultureInfoName.toString(), item.BillHours, 2)}
                                                        </td>
                                                        <td className='invoiceTD text-right'>
                                                            {item.CurrencyCode + ' ' + FormatInvoiceDecimalNumber(cultureInfoName.toString(), item.TotalAmount, decimalPlaces)}
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className='invoiceTfootRow'>
                                                {showEntryDateInInvoiceReport && <td></td>}
                                                {showProjectNameInInvoiceReport && <td></td>}
                                                {showEmployeeNameInInvoiceReport && <td></td>}
                                                {showBillingRateInInvoiceReport ? (
                                                    <>
                                                        <td></td>
                                                        <td className='invoiceTotalLabel'>Total:</td>
                                                    </>
                                                ) : (
                                                    <td className='invoiceTotalLabel'>Total:</td>
                                                )}
                                                <td className='invoiceTotalValue text-center'>
                                                    {FormatInvoiceDecimalNumber(cultureInfoName.toString(), masterArray.TotalBillHours, 2)}
                                                </td>
                                                <td className='invoiceTotalValue text-right'>
                                                    {masterArray.CurrencyCode + ' ' + FormatInvoiceDecimalNumber(cultureInfoName.toString(), masterArray.TotalTimesheetAmount, decimalPlaces)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                }
                                {invoiceDetail.expenseArray.length > 0 && <table className='invoiceTable mb-6'>
                                    <thead>
                                        <tr>
                                            {showProjectNameInInvoiceReport && <th className='invoiceGridTH' style={{ width: 167 }}>
                                                {getValueFromResource('Project Name', dispatch, t)}
                                            </th>}
                                            <th className='invoiceGridTH' style={{ width: 408 }}>
                                                {getValueFromResource('Expense Description', dispatch, t)}
                                            </th>
                                            <th className='invoiceGridTH' style={{ width: 104 }}>
                                                {getValueFromResource('Billed Amount', dispatch, t)}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoiceDetail.expenseArray.length > 0 && invoiceDetail.expenseArray.map((item: any, index: number) => {
                                            return (
                                                <tr key={index} className='invoiceBody'>
                                                    {showProjectNameInInvoiceReport && <td className='invoiceTD'>{item.ProjectName}</td>}
                                                    <td className='invoiceTD'>{item.Description}</td>
                                                    <td className='invoiceTD text-right'>{item.CurrencyCode + " " + FormatInvoiceDecimalNumber(cultureInfoName.toString(), item.BilledExpenseAmount, decimalPlaces)}</td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="invoiceTfootRow">
                                            {showProjectNameInInvoiceReport && <td></td>}
                                            <td className="invoiceTotalLabel text-right">
                                                Total:
                                            </td>
                                            <td className="invoiceTotalValue text-right">
                                                {masterArray.CurrencyCode +
                                                    " " +
                                                    FormatInvoiceDecimalNumber(
                                                        cultureInfoName.toString(),
                                                        masterArray.TotalExpenseAmount,
                                                        decimalPlaces
                                                    )}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>}
                                {/* <div style={{ textAlign: 'right' }}>
                                    <span className='invoice-info-right' style={{ width: 71, fontWeight: 'bold' }}>
                                        {getValueFromResource('Sub Total:', dispatch, t)}
                                    </span>
                                    <span className='invoice-info-right' style={{ width: 110, fontWeight: 'bold' }}>{masterArray.CurrencyCode + " " + FormatInvoiceDecimalNumber(cultureInfoName.toString(), masterArray.SubTotal, decimalPlaces)}</span><br />
                                    <span className='invoice-info-right' style={{ width: 71, fontWeight: 'bold' }}>
                                        {getValueFromResource('Discount', dispatch, t)}
                                    </span>
                                    <span className='invoice-info-right' style={{ width: 110, fontWeight: 'bold' }}>{"- " + masterArray.CurrencyCode + " " + FormatInvoiceDecimalNumber(cultureInfoName.toString(), masterArray.Discount, decimalPlaces)}</span><br />
                                    {taxArray.length > 0 && taxArray[0].TaxCodeName != "null" && taxArray.map((item: any, index: number) => {
                                        return (<><span className='invoice-info-right' style={{ width: 71, fontWeight: 'bold' }}>
                                            {getValueFromResource(item.TaxCodeName, dispatch, t) + ":"} </span>
                                            <span className='invoice-info-right' style={{ width: 110, fontWeight: 'bold' }}>{masterArray.CurrencyCode + " " + FormatInvoiceDecimalNumber(cultureInfoName.toString(), item.TotalTaxCode, decimalPlaces)}</span><br /></>
                                        )
                                    })}
                                    <span className='mt-2'></span>
                                    <span style={{ borderTop: '1px solid #000000', borderBottom: '1px solid #000000', padding: '3px 0' }}>
                                        <span className='invoice-info-right' style={{ width: 80, fontWeight: 'bold' }}>
                                            {getValueFromResource('Grand Total:', dispatch, t)}
                                        </span>
                                        <span className='invoice-info-right' style={{ width: 110, fontWeight: 'bold' }}>{masterArray.CurrencyCode + " " + FormatInvoiceDecimalNumber(cultureInfoName.toString(), masterArray.GrandTotal, decimalPlaces)}</span>
                                    </span>
                                </div> */}
                                <div className='invoice-totals-block' style={{ textAlign: 'right' }}>
                                    <div className='invoice-total-line'>
                                        <span className='invoice-info-right invoice-total-label'>
                                            {getValueFromResource('Sub Total:', dispatch, t)}
                                        </span>
                                        <span className='invoice-info-right invoice-total-value'>{masterArray.CurrencyCode + " " + FormatInvoiceDecimalNumber(cultureInfoName.toString(), masterArray.SubTotal, decimalPlaces)}</span>
                                    </div>
                                    <div className='invoice-total-line'>
                                        <span className='invoice-info-right invoice-total-label'>
                                            {getValueFromResource('Discount', dispatch, t)}
                                        </span>
                                        <span className='invoice-info-right invoice-total-value'>{"- " + masterArray.CurrencyCode + " " + FormatInvoiceDecimalNumber(cultureInfoName.toString(), masterArray.Discount, decimalPlaces)}</span>
                                    </div>
                                    {taxArray.length > 0 && taxArray[0].TaxCodeName != "null" && taxArray.map((item: any, index: number) => {
                                        return (
                                            <div className='invoice-total-line' key={index}>
                                                <span className='invoice-info-right invoice-total-label'>
                                                    {getValueFromResource(item.TaxCodeName, dispatch, t) + ":"}
                                                </span>
                                                <span className='invoice-info-right invoice-total-value'>{masterArray.CurrencyCode + " " + FormatInvoiceDecimalNumber(cultureInfoName.toString(), item.TotalTaxCode, decimalPlaces)}</span>
                                            </div>
                                        )
                                    })}
                                    <div className='invoice-total-line invoice-grand-total-line'>
                                        <span className='invoice-info-right invoice-total-label'>
                                            {getValueFromResource('Grand Total:', dispatch, t)}
                                        </span>
                                        <span className='invoice-info-right invoice-total-value'>{masterArray.CurrencyCode + " " + FormatInvoiceDecimalNumber(cultureInfoName.toString(), masterArray.GrandTotal, decimalPlaces)}</span>
                                    </div>
                                </div>
                                <div className='invoiceTableFooterDescription'>
                                    {Array.isArray(masterArray.MasterDescription)
                                        ? masterArray.MasterDescription
                                            .map((item: any) =>
                                                String(item ?? '').replace(/\t/g, '    ')
                                            )
                                            .join('\n')
                                        : String(masterArray.MasterDescription ?? '').replace(/\t/g, '    ')
                                    }
                                </div>
                            </>}
                        </div>
                        <div className="modal-footer">
                            <a onClick={() => { handleDownloadPdf() }} className={"btn btn-primary btn-sm me-2" + (isDownloadingPdf ? " disabled" : "")}>
                                <i className="bi bi-file-earmark-pdf fs-5 me-1"></i>
                                {getValueFromResource('Download PDF', dispatch, t)}
                            </a>
                            <a onClick={() => { handlePrint() }} className={"btn btn-primary btn-sm "}>
                                <i className="bi bi-printer fs-5 me-1"></i>
                                {getValueFromResource('Print Invoice', dispatch, t)}
                            </a>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'none' }}> <InvoicePrintWidgetCustom ref={componentRef}  {...invoiceDetail} /></div>
            </div>
        </>
    )
}

export default InvoicePrintViewWidgetCustom